import React from 'react';
import useSWR from "swr";
import {swrFetcher} from "../../utils/api.ts";
import {Link} from "wouter";
import RenderPlatformIcon from "../../components/RenderPlatformIcon.tsx";
import {IoCubeOutline} from "react-icons/io5";
import {AssetResponseItem} from "../../@types/AssetTypes.ts";
import RenderPostCaption from "../../components/ui/RenderPostCaption.tsx";
import {RenderPost} from "@/pages/posts/PostsHome.tsx";

function Dashboard() {
    return (
        <div>
            <h1 className={"text-4xl mb-2"}>Overview</h1>
            <div className={"grid grid-cols-1 gap-4 md:grid-cols-2"}>
                <AccountsList/>
                <NeedsAttention/>
                <UpcomingPosts/>
                <RecentPosts/>
                <Drafts/>
            </div>
        </div>
    );
}

function Drafts() {
    const {data, isLoading} = useSWR<{assets:AssetResponseItem[]}>('/assets?status=draft&limit=5', swrFetcher)
    return (
        <CardContainer title={"Drafts"}
            isLoading={isLoading}
                       isEmpty={data?.assets.length === 0}>   <div>
        <ul className={"divide-y divide-base-100"}>
            {
                data?.assets.map((item) => {
                    const data = Array.isArray(item.data) ? item.data[0] : item.data;
                    return <Link href={`/posts/edit/${item.id}`}
                                 className={"block px-2 py-1"} key={String(item.id)}>
                        {item.title || <RenderPostCaption caption={data.post.caption} />}
                    </Link>
                })
            }
        </ul>
        </div>
        </CardContainer>
    );
}

function NoContent({title}){
    return <div className="text-center flex flex-col items-center  card-body">
        <IoCubeOutline size={48} className={"text-muted"} style={{strokeWidth:1}} />
        <div className="mt-2 text-center">
            {title}
        </div>
     </div>
}

function CardContainer({isLoading, title, children, isEmpty}:{
    isLoading:boolean,
    children:React.ReactNode,
    isEmpty:boolean
    title: string,
}){
    return <div className={"card bg-base-100 border border-base-200 shadow-md overflow-hidden"}>
        <div className="px-4 py-4 border-b border-base-200">
            <div className="card-title">{title}</div>
        </div>
        {
            isLoading ? <div className={"text-center card-body"}>Loading...</div> : <div>
                {
                    isEmpty ? <NoContent title={"No data"} /> : <div>
                        {children}
                    </div>
                }
            </div>
        }
    </div>
}

function RecentPosts() {
    const {data, isLoading} = useSWR<{assets: AssetResponseItem[]}>('/assets?status=published&limit=5', swrFetcher)
    return (
        <CardContainer isLoading={isLoading} isEmpty={data?.assets.length === 0} title={"Published"}>
            <ul className={"divide-y divide-base-100"}>
                {
                    data?.assets.map((item) => {
                        const data = Array.isArray(item.data) ? item.data[0] : item.data;
                        return <Link href={`/posts/published/${item.id}`}
                                     style={{
                                                               }}
                                     className={"block"} key={String(item.id)}>
                            <div style={{
                                height:200,
                                overflow:'hidden',
                                display:'block',
                                maskImage: "linear-gradient(to bottom, black 100px, rgba(0, 0, 0, 1) 140px, rgba(0, 0, 0, 0) 200px)"
                            }}>
                                <RenderPost post={item} />
                            </div>

                            {/*{item.title || <RenderPostCaption caption={data.post.caption || data.post[0].caption} />}*/}
                        </Link>
                    })
                }
            </ul>
        </CardContainer>
    );
}


function UpcomingPosts() {
    const {data, isLoading} = useSWR<{assets: AssetResponseItem[]}>('/assets?status=pending&limit=5', swrFetcher)
    return (
        <CardContainer isLoading={isLoading} isEmpty={data?.assets.length === 0} title={"Upcoming"}>
             <ul className={"divide divide-y divide-base-200"}>
                {
                    data?.assets.map((item) => {
                        const data = 'post' in item.data ? (Array.isArray(item.data.post) ? item.data.post[0] : item.data.post) : null;
                        if(!data) return null;
                        return <Link
                                style={{
                                    height:200,
                                    overflow:'hidden',
                                    display:'block',
                                    maskImage: "linear-gradient(to bottom, black 100px, rgba(0, 0, 0, 1) 140px, rgba(0, 0, 0, 0) 200px)"
                                }}
                            href={`/posts/edit/${item.id}`}
                                     className={"block px-3 py-2"} key={String(item.id)}>
                            <RenderPost post={item} />
                        </Link>
                    })
                }
            </ul>
        </CardContainer>
    )
}


const NeedsAttention = ()=>{
    const {data, isLoading} = useSWR<{assets: AssetResponseItem[]}>('/assets?status=error&limit=5', swrFetcher);
    return <CardContainer title={"Needs attention"} isLoading={isLoading} isEmpty={data?.assets.length === 0}>
            <ul className={"divide divide-y divide-base-200"}>
                {
                    data?.assets?.map((item)=>{
                        const error = item.metadata?.data?.error?.message;
                        return <Link to={`/posts/edit/${item.id}`} className={"block px-3 py-2"} key={String(item.id)}>
                            {item.title}
                            <div className={"text-danger"}>{error}</div>
                        </Link>
                    })
                }
        </ul>
    </CardContainer>
}

const AccountsList = () => {
    const {data, isLoading} = useSWR('/accounts?status=active', swrFetcher);

    return <CardContainer isLoading={isLoading} isEmpty={data?.accounts.length === 0} title={"Accounts"}>
        <>
            <ul className="list">
                {
                    data?.accounts.slice(0, 5).map((i) => {
                        return <Link to={`/accounts/details/${i.id}`}
                                     className={`list-row flex gap-2 ${i.status === "error" ? 'text-danger' : ''} `}
                                     key={i.id}>
                            <RenderPlatformIcon platform={i.platform}/>
                            {i.username || i.name}
                        </Link>
                    })
                }

            </ul>
            {
                data?.accounts.length > 5 ?
                    <div className="text-center p-2"><Link href={"/settings"}>View all</Link></div> : null
            }
            <div className="card-footer text-center mb-2">
                <Link className={" btn-sm btn  btn-outline-primary"} href={"/accounts_connect"}>Connect accounts</Link>
            </div>

        </>
    </CardContainer>
}


export default Dashboard;
